import React, { useState, useEffect } from 'react';
import { Button } from '@cognite/cogs.js';
import {
  DigitalTwin,
  useCountDigitalTwinsQuery,
  useGetDigitalTwinsQuery,
} from 'types/dt-graphql-apollo-v2';
import { useAccessControl } from 'hooks/useAccessControl';
import { PAGINATION_PAGE_SIZE } from 'utils/config';
import { SimplePagination, TitleBar } from 'components';
import { DigitalTwinsCards, NewDigitalTwin, DigitalTwinsLoadingCards } from '.';
import { useStoreState } from 'hooks';

export const DigitalTwins = () => {
  const [newDigitalTwin, setNewDigitalTwin] = useState(false);

  const hasAccess = useAccessControl();
  const canCreateProject = hasAccess(['Create_DigitalTwin']);

  // Wait for authentication token to be available before executing queries
  const { loggedIn: isSignedIn, token } = useStoreState((state) => state.auth);
  const isAuthenticated = isSignedIn && !!token; // Ensure both login and token are ready
  const {
    data: digitalTwinsData,
    loading: digitalTwinsLoading,
    refetch: refetchDigitalTwins,
  } = useGetDigitalTwinsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      first: PAGINATION_PAGE_SIZE,
      cursor: null,
    },
    skip: !isAuthenticated, // Don't execute until user is authenticated with token
  });

  const {
    data: countData,
    loading: countLoading,
    refetch: refetchCount,
  } = useCountDigitalTwinsQuery({
    skip: !isAuthenticated, // Don't execute until user is authenticated with token
  });

  // Refetch data when authentication becomes ready
  useEffect(() => {
    if (isAuthenticated) {
      if (!digitalTwinsLoading && !digitalTwinsData) {
        refetchDigitalTwins();
      }
      if (!countLoading && !countData) {
        refetchCount();
      }
    }
  }, [
    isAuthenticated,
    digitalTwinsLoading,
    digitalTwinsData,
    refetchDigitalTwins,
    countLoading,
    countData,
    refetchCount,
  ]);

  const isLoading = digitalTwinsLoading || countLoading;

  const handlePageChange = async (cursor: string | null) => {
    await refetchDigitalTwins({
      first: PAGINATION_PAGE_SIZE,
      cursor,
    });
  };

  const digitalTwins = (digitalTwinsData?.listDigitalTwin?.items ||
    []) as DigitalTwin[];

  const startCursor =
    digitalTwinsData?.listDigitalTwin?.pageInfo.startCursor || null;
  const endCursor =
    digitalTwinsData?.listDigitalTwin?.pageInfo.endCursor || null;
  const totalCount =
    countData?.aggregateDigitalTwin?.items[0]?.count?.externalId || 0;

  const renderPageContent = () => (
    <div style={{ margin: '0 2rem' }}>
      {isLoading ? (
        <DigitalTwinsLoadingCards />
      ) : (
        <DigitalTwinsCards
          digitalTwins={digitalTwins}
          onCreateNewDigitalTwin={() => setNewDigitalTwin(true)}
          onUpdate={() => refetchDigitalTwins()}
        />
      )}

      <SimplePagination
        startCursor={startCursor}
        endCursor={endCursor}
        totalCount={totalCount}
        onChange={handlePageChange}
      />
    </div>
  );

  return (
    <>
      <TitleBar
        heading="Galactica"
        subheading="Manage, control and monitor your project data."
      >
        {canCreateProject && (
          <Button
            type="primary"
            icon="AddLarge"
            onClick={() => setNewDigitalTwin(true)}
          >
            Create new project
          </Button>
        )}
      </TitleBar>

      {renderPageContent()}

      {newDigitalTwin && (
        <NewDigitalTwin onCancel={() => setNewDigitalTwin(false)} />
      )}
    </>
  );
};
